import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { KEYS, sGet, sSet, sDel } from './keys'
import {
  SEED_IMAGES, SEED_ACTIVITY,
  SEED_WORK_PLANS, DEFAULT_SETTINGS,
} from './seed'

const StoreCtx = createContext(null)

export function useStore() {
  const ctx = useContext(StoreCtx)
  if (!ctx) throw new Error('useStore must be used within StoreProvider')
  return ctx
}

const API = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'

// ─── Fetch live children from the API, scoped to user's school ───────────────
async function fetchChildrenFromAPI(schoolId) {
  try {
    const token = localStorage.getItem('fslp:access')
    if (!token) return null

    // Always scope by school — superadmin can pass explicit schoolId,
    // teachers/admins get their school from their JWT on the backend anyway,
    // but passing it explicitly makes the intent clear and avoids cross-school leaks.
    const schoolQ = schoolId ? `&school=${schoolId}` : ''
    const res = await fetch(`${API}/children?limit=200${schoolQ}`, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
    if (!res.ok) return null

    const data = await res.json()
    if (!data.success) return null

    const normalised = (data.children || []).map(c => ({
      id:         c._id,
      _id:        c._id,
      name:       c.name,
      color:      c.color || '#1A4A6E',
      initials:   c.name.trim().split(/\s+/).map(w => w[0] || '').join('').slice(0, 2).toUpperCase(),
      age:        c.dob
        ? Math.max(0, new Date().getFullYear() - new Date(c.dob).getFullYear())
        : null,
      disability: c.disabilities || [],
      classroom:  c.classroom || null,
      ...c,
    }))

    return { children: normalised, total: data.total ?? normalised.length }
  } catch {
    return null
  }
}

export function StoreProvider({ children }) {
  const [collection,  setCollection]  = useState(null)
  const [childList,   setChildList]   = useState(null)
  const [childTotal,  setChildTotal]  = useState(null)
  const [progress,    setProgress]    = useState(null)
  const [activity,    setActivity]    = useState(null)
  const [workPlans,   setWorkPlans]   = useState(null)
  const [settings,    setSettings]    = useState(null)
  const [loaded,      setLoaded]      = useState(false)
  const [saveStatus,  setSaveStatus]  = useState('idle')

  // schoolId is set once after auth resolves — threads into all child fetches
  const [schoolId, setSchoolId] = useState(null)

  // ─── Boot ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    ;(async () => {
      const [col, prog, act, wpl, stt] = await Promise.all([
        sGet(KEYS.COLLECTION),
        sGet(KEYS.PROGRESS),
        sGet(KEYS.ACTIVITY),
        sGet(KEYS.WORK_PLANS),
        sGet(KEYS.SETTINGS),
      ])

      const isFirst = !col

      setCollection(col  || SEED_IMAGES)
      setProgress(prog   || {})
      setActivity(act    || SEED_ACTIVITY)
      setWorkPlans(wpl   || SEED_WORK_PLANS)
      setSettings(stt    || DEFAULT_SETTINGS)

      if (isFirst) {
        await Promise.all([
          sSet(KEYS.COLLECTION,  SEED_IMAGES),
          sSet(KEYS.PROGRESS,    {}),
          sSet(KEYS.ACTIVITY,    SEED_ACTIVITY),
          sSet(KEYS.WORK_PLANS,  SEED_WORK_PLANS),
          sSet(KEYS.SETTINGS,    DEFAULT_SETTINGS),
        ])
      }

      // Resolve school from stored user object
      // AuthContext stores the user in localStorage as 'fslp:user'
      let resolvedSchoolId = null
      try {
        const raw = localStorage.getItem('fslp:user')
        if (raw) {
          const u = JSON.parse(raw)
          resolvedSchoolId = u?.school || null
          setSchoolId(resolvedSchoolId)
        }
      } catch {}

      const apiResult = await fetchChildrenFromAPI(resolvedSchoolId)
      if (apiResult) {
        setChildList(apiResult.children)
        setChildTotal(apiResult.total)
      } else {
        const cached = await sGet(KEYS.CHILDREN)
        setChildList(cached || [])
        setChildTotal((cached || []).length)
      }

      setLoaded(true)
    })()
  }, [])

  // ─── refreshChildren ───────────────────────────────────────────────────────
  // Call after any child mutation. Accepts an optional override schoolId
  // (useful when superadmin is viewing a specific school).
  const refreshChildren = useCallback(async (overrideSchoolId) => {
    const sid = overrideSchoolId ?? schoolId
    const apiResult = await fetchChildrenFromAPI(sid)
    if (apiResult) {
      setChildList(apiResult.children)
      setChildTotal(apiResult.total)
    }
  }, [schoolId])

  // Call this after login resolves so the store re-fetches with the correct school
  const setUserSchool = useCallback(async (sid) => {
    setSchoolId(sid)
    const apiResult = await fetchChildrenFromAPI(sid)
    if (apiResult) {
      setChildList(apiResult.children)
      setChildTotal(apiResult.total)
    }
  }, [])

  // ─── Persist helper ────────────────────────────────────────────────────────
  const persist = useCallback(async (key, val, setter) => {
    setter(val)
    setSaveStatus('saving')
    const ok = await sSet(key, val)
    setSaveStatus(ok ? 'saved' : 'error')
    setTimeout(() => setSaveStatus('idle'), 2200)
  }, [])

  // ─── Activity log ──────────────────────────────────────────────────────────
  const logActivity = useCallback((entry) => {
    setActivity(prev => {
      const next = [
        { id: `ac-${Date.now()}`, time: new Date().toISOString(), ...entry },
        ...(prev || []),
      ].slice(0, 50)
      sSet(KEYS.ACTIVITY, next)
      return next
    })
  }, [])

  // ─── Collection actions ────────────────────────────────────────────────────
  const addImage         = useCallback((img)        => persist(KEYS.COLLECTION, [img, ...(collection || [])],                                         setCollection), [collection, persist])
  const updateImage      = useCallback((img)        => persist(KEYS.COLLECTION, (collection || []).map(i => i.id === img.id ? img : i),               setCollection), [collection, persist])
  const deleteImage      = useCallback((id)         => persist(KEYS.COLLECTION, (collection || []).filter(i => i.id !== id),                          setCollection), [collection, persist])
  const bulkUpdateImages = useCallback((ids, patch) => persist(KEYS.COLLECTION, (collection || []).map(i => ids.has(i.id) ? { ...i, ...patch } : i),  setCollection), [collection, persist])

  // ─── Child actions ─────────────────────────────────────────────────────────
  const addChild    = useCallback((c) => {
    setChildList(prev => [...(prev || []), c])
    setChildTotal(prev => (prev ?? 0) + 1)
  }, [])

  const updateChild = useCallback((c) => {
    setChildList(prev => (prev || []).map(x => (x.id === c.id || x._id === c._id) ? c : x))
  }, [])

  const deleteChild = useCallback((id) => {
    setChildList(prev => (prev || []).filter(x => x.id !== id && x._id !== id))
    setChildTotal(prev => Math.max(0, (prev ?? 1) - 1))
  }, [])

  // ─── Progress actions ──────────────────────────────────────────────────────
  const updateProgressRecord = useCallback((childId, domainId, indId, val) => {
    const next = {
      ...(progress || {}),
      [childId]: {
        ...((progress || {})[childId] || {}),
        [domainId]: {
          ...(((progress || {})[childId] || {})[domainId] || {}),
          [indId]: val,
        },
      },
    }
    persist(KEYS.PROGRESS, next, setProgress)
  }, [progress, persist])

  // ─── Work plan actions ─────────────────────────────────────────────────────
  const saveWorkPlan = useCallback((plan) => {
    const idx = (workPlans || []).findIndex(p => p.id === plan.id)
    const next = idx >= 0
      ? (workPlans || []).map(p => p.id === plan.id ? plan : p)
      : [plan, ...(workPlans || [])]
    persist(KEYS.WORK_PLANS, next, setWorkPlans)
  }, [workPlans, persist])

  const deleteWorkPlan = useCallback((id) =>
    persist(KEYS.WORK_PLANS, (workPlans || []).filter(p => p.id !== id), setWorkPlans),
  [workPlans, persist])

  // ─── Settings actions ──────────────────────────────────────────────────────
  const updateSettings = useCallback((patch) => {
    const next = { ...(settings || DEFAULT_SETTINGS), ...patch }
    persist(KEYS.SETTINGS, next, setSettings)
  }, [settings, persist])

  // ─── Reset ─────────────────────────────────────────────────────────────────
  const resetAll = useCallback(async () => {
    await Promise.all(Object.values(KEYS).map(k => sDel(k)))
    window.location.reload()
  }, [])

  return (
    <StoreCtx.Provider value={{
      loaded, saveStatus,
      schoolId, setUserSchool,
      collection,  addImage, updateImage, deleteImage, bulkUpdateImages,
      childList,   childTotal, addChild, updateChild, deleteChild, refreshChildren,
      progress,    updateProgressRecord,
      activity,    logActivity,
      workPlans,   saveWorkPlan, deleteWorkPlan,
      settings,    updateSettings,
      resetAll,
    }}>
      {children}
    </StoreCtx.Provider>
  )
}